import './solution.css'
import { useI18n } from '../lib/i18n'
import { Reveal } from '../components/Reveal'
import { SectionHeader } from '../components/SectionHeader'
import { HudCard } from '../components/HudCard'
import { MonoTag } from '../components/MonoTag'
import { MetricTicker } from '../components/MetricTicker'

// 02 · GIẢI PHÁP — "Lot Passport": verifiable, per-lot compliance.
// LEFT: prose + leaf checklist. RIGHT: a passport-preview HudCard with mono
// key/value rows, a verdict badge (the one place a glow is allowed) and a
// provenance footer + GeoJSON hash.
export default function Solution() {
  const { lang } = useI18n()
  const C = ({
    vi: {
      kicker: 'GIẢI PHÁP',
      title: (
        <>
          Lot Passport — tuân thủ <em>kiểm chứng được</em>, từng lô.
        </>
      ),
      sub: 'Mỗi lô cà phê đi kèm một hồ sơ tuân thủ EUDR khép kín: từ ranh thửa, đối chiếu vệ tinh, đến phán quyết và dấu vết kiểm toán bất biến.',
      lead: 'Thay vì một tờ cam kết, ARGO sinh ra bằng chứng. Từng lô được định vị, đối chiếu với dữ liệu mất rừng độc lập, rồi đóng dấu phán quyết kèm hash để bất kỳ ai cũng kiểm lại được.',
      checks: [
        <>Định vị ranh thửa và lưu dưới dạng đa giác GeoJSON.</>,
        <>
          Đối chiếu mất rừng sau mốc <strong>31/12/2020</strong> trên Sentinel-2, GFW và JRC TMF.
        </>,
        <>
          Phán quyết ba mức: <span className="solution-v solution-v--green">xanh</span> ·{' '}
          <span className="solution-v solution-v--amber">vàng</span> ·{' '}
          <span className="solution-v solution-v--red">đỏ</span>.
        </>,
        <>Xuất GeoJSON sẵn sàng nộp lên TRACES NT cho nhà nhập khẩu EU.</>,
        <>Dấu vết kiểm toán bất biến, băm SHA-256 cho mỗi hồ sơ.</>,
      ],
      cardHeader: 'LOT PASSPORT · PREVIEW',
      rows: [
        { k: 'LOT ID', v: 'CMG-04', mono: true },
        { k: 'DIỆN TÍCH', v: '4.12 ha', mono: true },
        { k: 'NGUỒN', v: 'SENTINEL-2 10m', mono: true },
      ],
      lossLabel: 'MẤT RỪNG',
      verdict: 'Đạt',
      provenance: 'Nguồn: Sentinel-2 · JRC TMF · GFC · GFW · Whisp',
      hashLabel: 'GEOJSON SHA-256',
    },
    en: {
      kicker: 'SOLUTION',
      title: (
        <>
          Lot Passport — <em>verifiable</em> compliance, per lot.
        </>
      ),
      sub: 'Every coffee lot ships with a self-contained EUDR compliance file: from plot boundary to satellite cross-check to verdict and an immutable audit trail.',
      lead: 'Instead of a signed promise, ARGO produces evidence. Each lot is geolocated, cross-checked against independent deforestation data, then stamped with a verdict and a hash anyone can re-verify.',
      checks: [
        <>Geolocate the plot boundary, stored as a GeoJSON polygon.</>,
        <>
          Cross-check deforestation after the <strong>2020-12-31</strong> cutoff on Sentinel-2, GFW and JRC TMF.
        </>,
        <>
          Three-tier verdict: <span className="solution-v solution-v--green">green</span> ·{' '}
          <span className="solution-v solution-v--amber">amber</span> ·{' '}
          <span className="solution-v solution-v--red">red</span>.
        </>,
        <>Export TRACES NT-ready GeoJSON for the EU importer.</>,
        <>Immutable audit trail, SHA-256 hashed per file.</>,
      ],
      cardHeader: 'LOT PASSPORT · PREVIEW',
      rows: [
        { k: 'LOT ID', v: 'CMG-04', mono: true },
        { k: 'AREA', v: '4.12 ha', mono: true },
        { k: 'SOURCE', v: 'SENTINEL-2 10m', mono: true },
      ],
      lossLabel: 'FOREST LOSS',
      verdict: 'PASS',
      provenance: 'Source: Sentinel-2 · JRC TMF · GFC · GFW · Whisp',
      hashLabel: 'GEOJSON SHA-256',
    },
  } as const)[lang]

  return (
    <section id="solution" className="section solution">
      <div className="container">
        <SectionHeader index="02" kicker={C.kicker} title={C.title} sub={C.sub} align="left" />

        <div className="solution-grid">
          {/* LEFT — prose + checklist */}
          <Reveal delay={0.06} className="solution-left">
            <p className="solution-lead">{C.lead}</p>
            <ul className="solution-checks">
              {C.checks.map((item, i) => (
                <li className="solution-check" key={i}>
                  <span className="solution-check-mark" aria-hidden>
                    <svg viewBox="0 0 16 16" width="14" height="14">
                      <path
                        d="M3 8.5l3.2 3.2L13 4.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span className="solution-check-text">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          {/* RIGHT — passport preview */}
          <Reveal delay={0.14} className="solution-right">
            <HudCard header={<>{C.cardHeader}</>} brackets className="solution-passport">
              <dl className="solution-rows">
                {C.rows.map((r) => (
                  <div className="solution-row" key={r.k}>
                    <dt className="solution-row-k label">{r.k}</dt>
                    <dd className="solution-row-v mono">{r.v}</dd>
                  </div>
                ))}
                <div className="solution-row">
                  <dt className="solution-row-k label">{C.lossLabel}</dt>
                  <dd className="solution-row-v solution-row-v--loss mono">
                    <MetricTicker value={0} decimals={1} suffix="%" />
                  </dd>
                </div>
              </dl>

              <div className="solution-verdict-wrap">
                <span className="solution-verdict">
                  <span className="solution-verdict-dot" aria-hidden />
                  {C.verdict} · PASS
                </span>
              </div>

              <div className="solution-foot">
                <p className="solution-prov mono">{C.provenance}</p>
                <p className="solution-hash mono">
                  <span className="tag solution-hash-label">{C.hashLabel}</span>
                  <span className="solution-hash-val">#a1f0…e3</span>
                </p>
              </div>

              <MonoTag className="solution-live">
                PREVIEW
              </MonoTag>
            </HudCard>
          </Reveal>
        </div>
      </div>
    </section>
  )
}
